<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { TableFoldProps as BookedJobLogTableFoldProps } from '@/components/tableFolds/BookedJobLogTableFold.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed, ref } from 'vue';

import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import BookedJobLogTableFold from '@/components/tableFolds/BookedJobLogTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  uniqueKey: string;
  jobId: number;
  jobDate: string;
  source: string;
  status: string;
  createdBy: string;
  createdAt: string;
  fold: BookedJobLogTableFoldProps;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const openFoldKey = ref('');

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  return [
    {
      value: 'jobId',
      title: 'Id',
      colspan: 2,
    },
    {
      value: 'jobDate',
      title: 'Date',
    },
    {
      value: 'source',
      title: 'Source',
    },
    {
      value: 'status',
      title: 'Status',
    },
    {
      value: 'createdBy',
      title: 'Created by',
    },
    {
      value: 'createdAt',
      title: 'Created at',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleFoldClick(uniqueKey: string): void {
  openFoldKey.value = openFoldKey.value === uniqueKey ? '' : uniqueKey;
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.stickyHeader,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.bookedJobsTable,
    ]"
    data-testid="bookedJobsTable"
  >
    <colgroup>
      <col :class="$style.foldCol" />
      <col :class="$style.jobIdCol" />
      <col :class="$style.jobDateCol" />
      <col :class="$style.sourceCol" />
      <col :class="$style.statusCol" />
      <col :class="$style.createdByCol" />
      <col :class="$style.createdAtCol" />
    </colgroup>
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText>No booked jobs</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.uniqueKey">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
          ]"
        >
          <td>
            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.uniqueKey"
              @click="handleFoldClick(dataItem.uniqueKey)"
            />
          </td>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.sourceTd">
            {{ dataItem.source }}
          </td>
          <td :class="$style.statusTd">
            {{ dataItem.status }}
          </td>
          <td :class="$style.createdByTd">
            {{ dataItem.createdBy }}
          </td>
          <td :class="$style.createdAtTd">
            {{ dataItem.createdAt }}
          </td>
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.uniqueKey">
              <BookedJobLogTableFold v-bind="dataItem.fold" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.bookedJobsTable {
  // Sum of the colgroup widths below, so the empty/loading state doesn't collapse to a sliver.
  width: 100%;
  min-width: variables.$bookedJobsTableWidth;
  table-layout: fixed;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
  .foldCol {
    width: 40px;
  }
  .jobIdCol {
    width: 80px;
  }
  .jobDateCol {
    width: 100px;
  }
  .sourceCol {
    width: 150px;
  }
  .statusCol {
    width: 140px;
  }
  .createdByCol {
    width: 300px;
  }
  .createdAtCol {
    width: 250px;
  }
  .jobIdTh {
    text-align: left;
  }
  .jobDateTh {
  }
  .sourceTh {
  }
  .statusTh {
  }
  .createdByTh {
  }
  .createdAtTh {
  }
  .jobIdTd {
    text-align: left;
  }
  .jobDateTd {
  }
  .sourceTd {
  }
  .statusTd {
  }
  .createdByTd {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .createdAtTd {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
